/**
 * React hook for monitoring wallet disconnection events
 * Detects when wallet extensions disconnect and notifies the application
 * 
 * Supports:
 * - OKX (Ethereum, Solana, Bitcoin)
 * - Phantom (Solana)
 * - OneKey (Solana)
 * - Binance (Ethereum)
 * - Ethereum providers via EIP-1193 (MetaMask, Phantom Ethereum, OneKey Ethereum, OKX Ethereum, Binance)
 */

import { useEffect, useRef } from 'react';
import {
  setupOKXWalletDisconnectDetection,
  WalletDisconnectCallback as OKXWalletDisconnectCallback,
} from '../sdk/signing/okx';
import {
  setupPhantomSolanaDisconnectDetection,
  setupOneKeySolanaDisconnectDetection,
  setupBinanceSolanaDisconnectDetection,
  SolanaWalletDisconnectCallback,
} from '../sdk/signing/solana';

/**
 * Wallet disconnection event
 */
export interface WalletDisconnectEvent {
  walletName: 'OKX' | 'Phantom' | 'OneKey' | 'MetaMask' | 'Binance' | 'Unknown';
  chainType: 'ethereum' | 'solana' | 'bitcoin';
  reason?: string;
}

/**
 * Configuration for wallet disconnection detection
 */
export interface UseWalletDisconnectDetectionConfig {
  /**
   * Callback function called when a wallet disconnects
   */
  onDisconnect?: (event: WalletDisconnectEvent) => void;
  
  /**
   * Enable detection for OKX wallets
   */
  enableOKX?: boolean;
  
  /**
   * Enable detection for Phantom wallets
   */
  enablePhantom?: boolean;
  
  /**
   * Enable detection for OneKey wallets
   */
  enableOneKey?: boolean;
  
  /**
   * Enable detection for Ethereum providers (EIP-1193)
   */
  enableEthereum?: boolean;
}

/**
 * Hook for monitoring wallet disconnection events
 * 
 * @param config - Configuration object
 * @returns Object with cleanup function (for manual cleanup if needed)
 */
export function useWalletDisconnectDetection(
  config: UseWalletDisconnectDetectionConfig = {}
): { cleanup: () => void } {
  const {
    onDisconnect,
    enableOKX = true,
    enablePhantom = true,
    enableOneKey = true,
    enableEthereum = true,
  } = config;

  const cleanupFunctionsRef = useRef<Array<() => void>>([]);

  useEffect(() => {
    if (!onDisconnect) {
      return; // No callback provided, skip setup
    }

    const cleanupFunctions: Array<() => void> = [];

    // Set up OKX wallet disconnection detection
    if (enableOKX) {
      const okxCallback: OKXWalletDisconnectCallback = (chainType, reason) => {
        onDisconnect({
          walletName: 'OKX',
          chainType,
          reason,
        });
      };
      cleanupFunctions.push(setupOKXWalletDisconnectDetection(okxCallback));
    }

    // Set up Phantom Solana disconnection detection
    if (enablePhantom) {
      const phantomCallback: SolanaWalletDisconnectCallback = (_walletName, reason) => {
        onDisconnect({
          walletName: 'Phantom',
          chainType: 'solana',
          reason,
        });
      };
      cleanupFunctions.push(setupPhantomSolanaDisconnectDetection(phantomCallback));
    }

    // Set up OneKey Solana disconnection detection
    if (enableOneKey) {
      const oneKeyCallback: SolanaWalletDisconnectCallback = (_walletName, reason) => {
        onDisconnect({
          walletName: 'OneKey',
          chainType: 'solana',
          reason,
        });
      };
      cleanupFunctions.push(setupOneKeySolanaDisconnectDetection(oneKeyCallback));
    }

    // Set up Binance Solana disconnection detection
    if (enableEthereum) { // Binance uses Ethereum detection flag since it's primarily Ethereum
      const binanceCallback: SolanaWalletDisconnectCallback = (_walletName, reason) => {
        onDisconnect({
          walletName: 'Binance',
          chainType: 'solana',
          reason,
        });
      };
      cleanupFunctions.push(setupBinanceSolanaDisconnectDetection(binanceCallback));
    }

    // Set up Ethereum provider disconnection detection (EIP-1193)
    if (enableEthereum && typeof window !== 'undefined') {
      const ethereumProvider = (window as any).ethereum;
      
      if (ethereumProvider && ethereumProvider.on) {
        // Detect which wallet is being used
        const detectWalletName = (): 'OKX' | 'OneKey' | 'Phantom' | 'MetaMask' | 'Binance' | 'Unknown' => {
          if ((window as any).okxwallet?.ethereum || ethereumProvider.isOKX) {
            return 'OKX';
          }
          if ((window as any).$onekey?.ethereum || ethereumProvider.isOneKey) {
            return 'OneKey';
          }
          if ((window as any).phantom?.ethereum || ethereumProvider.isPhantom) {
            return 'Phantom';
          }
          if ((window as any).BinanceChain || ethereumProvider.isBinance) {
            return 'Binance';
          }
          if (ethereumProvider.isMetaMask) {
            return 'MetaMask';
          }
          return 'Unknown';
        };

        const handleDisconnect = (error: { code: number; message: string }) => {
          const walletName = detectWalletName();
          console.warn(`[Wallet] ${walletName} Ethereum wallet disconnected:`, error);
          onDisconnect({
            walletName,
            chainType: 'ethereum',
            reason: error.message || 'Wallet disconnected',
          });
        };

        const handleAccountsChanged = (accounts: string[]) => {
          if (accounts.length === 0) {
            const walletName = detectWalletName();
            console.warn(`[Wallet] ${walletName} Ethereum wallet accounts cleared (disconnected)`);
            onDisconnect({
              walletName,
              chainType: 'ethereum',
              reason: 'Accounts cleared',
            });
          }
        };

        ethereumProvider.on('disconnect', handleDisconnect);
        ethereumProvider.on('accountsChanged', handleAccountsChanged);

        cleanupFunctions.push(() => {
          if (ethereumProvider.removeListener) {
            ethereumProvider.removeListener('disconnect', handleDisconnect);
            ethereumProvider.removeListener('accountsChanged', handleAccountsChanged);
          }
        });
      }
    }

    // Store cleanup functions
    cleanupFunctionsRef.current = cleanupFunctions;

    // Return cleanup function
    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
      cleanupFunctionsRef.current = [];
    };
  }, [onDisconnect, enableOKX, enablePhantom, enableOneKey, enableEthereum]);

  return {
    cleanup: () => {
      cleanupFunctionsRef.current.forEach(cleanup => cleanup());
      cleanupFunctionsRef.current = [];
    },
  };
}
